/** Cosmetic face turns, driven only by changes in authoritative observations. */
import type { PuzzleState } from "@pd/shared";
import { flipTileTarget } from "@pd/shared";

export const FLIP_TILE_ANIMATION_MS = 320;
export const FLIP_TILE_COLOURS = ["#586fac", "#96598f"] as const;

interface Face {
  colour: number;
  from: number;
  started: number | null;
}
export interface TileFlipVisual {
  colour: number;
  scale: number;
  lift: number;
}

export class FlipTileAnimations {
  private faces = new Map<number, Face>();

  ingest(puzzles: readonly PuzzleState[], now: number): void {
    const seen = new Set<number>();
    for (const puzzle of puzzles) {
      if (puzzle.kind !== "flip-tiles") continue;
      for (let index = 0; index < puzzle.colours.length; index++) {
        const id = flipTileTarget(puzzle.id, index);
        const colour = puzzle.colours[index]!;
        seen.add(id);
        const old = this.faces.get(id);
        if (!old) this.faces.set(id, { colour, from: colour, started: null });
        else if (old.colour !== colour) {
          const visible = this.sample(id, now)!.colour;
          this.faces.set(id, { colour, from: visible, started: now });
        }
      }
    }
    for (const id of this.faces.keys()) if (!seen.has(id)) this.faces.delete(id);
  }

  sample(id: number, now: number): TileFlipVisual | null {
    const face = this.faces.get(id);
    if (!face) return null;
    const progress = face.started === null ? 1 : Math.max(0, Math.min(1, (now - face.started) / FLIP_TILE_ANIMATION_MS));
    if (progress === 1) return { colour: face.colour, scale: 1, lift: 0 };
    const eased = progress * progress * (3 - 2 * progress);
    return {
      colour: progress < 0.5 ? face.from : face.colour,
      scale: Math.max(0.04, Math.abs(Math.cos(eased * Math.PI))),
      lift: Math.sin(eased * Math.PI) * 9,
    };
  }

  clear(): void { this.faces.clear(); }
}
